import { useMemo } from "react";
import { ZONE_COORDS } from "@/utils/constants";
import { getDistanceMiles } from "@/utils/geo";

/**
 * Compact SVG showing the user's zip as a red pin at center,
 * with covered cities plotted as small navy dots relative to it.
 * No external map deps — uses ZONE_COORDS and equirectangular projection
 * scaled to fit within the given radius (rings drawn every ~radius/4 miles).
 */
export function CoverageMiniMap({ centerLat, centerLng, radiusMi, cities = [], width = 260, height = 160 }) {
  const margin = 12;
  const data = useMemo(() => {
    if (!centerLat || !centerLng) return null;
    const halfMinPx = Math.min(width, height) / 2 - margin;
    const points = [];
    for (const name of cities) {
      const c = ZONE_COORDS[name];
      if (!c) continue;
      const [lat, lng] = c;
      const dist = getDistanceMiles(centerLat, centerLng, lat, lng);
      const dLatMi = (lat - centerLat) * 69.0;
      const dLngMi = (lng - centerLng) * 69.0 * Math.cos((centerLat * Math.PI) / 180);
      const px = width / 2 + (dLngMi / radiusMi) * halfMinPx;
      const py = height / 2 - (dLatMi / radiusMi) * halfMinPx;
      if (dist > radiusMi + 0.5) continue;
      points.push({ name, x: px, y: py, dist });
    }
    return { points, halfMinPx };
  }, [centerLat, centerLng, radiusMi, cities, width, height]);

  if (!data) return null;

  const cx = width / 2;
  const cy = height / 2;
  const rings = [0.25, 0.5, 0.75, 1.0];

  return (
    <div className="rounded-lg overflow-hidden border border-slate-200 bg-gradient-to-br from-slate-50 to-white" data-testid="coverage-mini-map">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block w-full h-auto">
        {rings.map((r) => (
          <circle
            key={r}
            cx={cx}
            cy={cy}
            r={data.halfMinPx * r}
            fill="none"
            stroke={r === 1 ? "#94a3b8" : "#e2e8f0"}
            strokeWidth={r === 1 ? 1 : 0.75}
            strokeDasharray={r === 1 ? "3 3" : "2 3"}
          />
        ))}
        <line x1={margin} y1={cy} x2={width - margin} y2={cy} stroke="#e2e8f0" strokeWidth={0.5} />
        <line x1={cx} y1={margin} x2={cx} y2={height - margin} stroke="#e2e8f0" strokeWidth={0.5} />
        {data.points.map((p) => (
          <g key={p.name}>
            <title>{`${p.name} — ${p.dist.toFixed(1)} mi`}</title>
            <circle cx={p.x} cy={p.y} r={2.75} fill="#0A192F" opacity={0.85} />
          </g>
        ))}
        <circle cx={cx} cy={cy} r={7} fill="#B91C1C" fillOpacity={0.25} />
        <circle cx={cx} cy={cy} r={3.5} fill="#B91C1C" />
        <text x={cx + 4} y={cy - data.halfMinPx + 10} fill="#64748b" fontSize={9} fontFamily="ui-sans-serif" textAnchor="start">{radiusMi} mi</text>
      </svg>
      <div className="flex items-center justify-between px-2.5 py-1.5 bg-slate-50 border-t border-slate-200">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-2 h-2 rounded-full bg-brand-red" />
          <span className="font-body text-[10px] text-gray-500">Your zip</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-2 h-2 rounded-full bg-navy" />
          <span className="font-body text-[10px] text-gray-500">{data.points.length} covered {data.points.length === 1 ? "city" : "cities"}</span>
        </div>
      </div>
    </div>
  );
}
